import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import {
  App,
  Card,
  Empty,
  Flex,
  Table,
  Tag,
  Button,
  Typography,
  Tooltip,
  Input,
  Select,
  DatePicker,
  Avatar,
  theme
} from 'antd';
import {
  VideoCameraOutlined,
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  ClearOutlined,
  SearchOutlined,
  CopyOutlined,
  EyeOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { AutoRefreshToggle } from '@/components/ui/AutoRefreshToggle';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { AUTOREFRESH_INTERVAL } from '@/constants/autorefresh';
import { createFileRoute } from '@tanstack/react-router';
import { useListAppointmentsQuery } from '@/api/mcp-appointments';
import type { Appointment, AppointmentStatus } from '@/api/mcp-appointments';
import { getMeetingInfo } from '@/components/appointments/extensions/extension-utils';
import {
  getAppointmentStatusLabel,
  getStatusTagColor
} from '@/components/appointments/calendar/calendarUtils';
import { useAppointments } from '@/contexts/AppointmentContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useLastUpdatedText } from '@/hooks/useLastUpdatedText';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { asAppointmentWithContext, getAppointmentDurationMinutes, getClientDisplayName, getClientEmail, getMeetingTitle, getMeetingDescription } from '@/components/appointments/extensions/jobInterview/utils';
import { useResponsivePinnedColumn } from '@/hooks/useResponsivePinnedColumn';

dayjs.extend(relativeTime);

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const Route = createFileRoute('/manage/video-meetings')({
  component: VideoMeetingsComponent
});

function VideoMeetingsComponent() {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();
  const { openDetails } = useAppointments();
  const { message } = App.useApp();

  const [filters, setFilters] = useState({
    status: undefined as AppointmentStatus | undefined,
    search: '',
    dateRange: undefined as [dayjs.Dayjs, dayjs.Dayjs] | undefined
  });
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  const listParams = useMemo(() => {
    const params: {
      format: 'VIDEO';
      startDate?: string;
      endDate?: string;
      status?: AppointmentStatus;
    } = {
      format: 'VIDEO'
    };

    if (filters.dateRange?.[0] && filters.dateRange?.[1]) {
      params.startDate = filters.dateRange[0].startOf('day').toISOString();
      params.endDate = filters.dateRange[1].endOf('day').toISOString();
    }

    if (filters.status) {
      params.status = filters.status;
    }

    return params;
  }, [filters.dateRange, filters.status]);

  const { data: allAppointments, isLoading, isFetching, dataUpdatedAt } = useListAppointmentsQuery(
    listParams,
    true,
    autoRefresh ? AUTOREFRESH_INTERVAL : false
  );

  const lastUpdatedText = useLastUpdatedText(dataUpdatedAt);

  const appointments = useMemo(() => {
    if (!allAppointments) return [];

    let videoAppointments = allAppointments;

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      videoAppointments = videoAppointments.filter((apt) => {
        const title = getMeetingTitle(apt, t);
        const clientName = getClientDisplayName(apt, t);
        const clientEmail = getClientEmail(apt) || '';

        return (
          title.toLowerCase().includes(searchLower) ||
          clientName.toLowerCase().includes(searchLower) ||
          clientEmail.toLowerCase().includes(searchLower) ||
          apt.anonymousPhone?.toLowerCase().includes(searchLower) ||
          apt.notes?.toLowerCase().includes(searchLower)
        );
      });
    }

    return [...videoAppointments].sort(
      (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
    );
  }, [allAppointments, filters.search, t]);

  const handleJoinMeeting = (appointment: Appointment) => {
    const meeting = getMeetingInfo(asAppointmentWithContext(appointment));
    const joinUrl = meeting.meetingModeratorUrl || meeting.meetingUrl;
    if (joinUrl) {
      window.open(joinUrl, '_blank');
    } else {
      message.warning(t('videoMeetings.messages.noMeetingUrl'));
    }
  };

  const { copy } = useCopyToClipboard();
  const handleCopyUrl = async (appointment: Appointment) => {
    const meeting = getMeetingInfo(asAppointmentWithContext(appointment));
    const url = meeting.meetingUrl || meeting.meetingModeratorUrl;
    if (!url) {
      message.warning(t('videoMeetings.messages.noMeetingUrl'));
      return;
    }
    await copy(url, {
      successMessage: t('videoMeetings.messages.urlCopied'),
      errorMessage: t('videoMeetings.messages.urlCopyFailed')
    });
  };

  const handleViewDetails = (
    appointment: Appointment,
    event: React.MouseEvent<HTMLElement>
  ) => {
    openDetails(appointment, event.currentTarget);
  };

  const pinColumn = useResponsivePinnedColumn();
  const columns = [
    {
      title: t('videoMeetings.table.client'),
      key: 'client',
      render: (_: unknown, record: Appointment) => {
        const clientName = getClientDisplayName(record, t);
        const clientEmail = getClientEmail(record);
        return (
          <Flex align="center" gap={token.marginSM} style={{ minWidth: 0, maxWidth: 300 }}>
            <Avatar size={36} icon={<UserOutlined />} style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <Text strong style={{ display: 'block' }} ellipsis={{ tooltip: clientName }}>
                {clientName}
              </Text>
              {clientEmail && (
                <Text type="secondary" style={{ display: 'block', fontSize: token.fontSizeSM }} ellipsis={{ tooltip: clientEmail }}>
                  <MailOutlined style={{ marginInlineEnd: token.marginXXS }} />
                  {clientEmail}
                </Text>
              )}
              {record.anonymousPhone && (
                <Text type="secondary" style={{ display: 'block', fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}>
                  <PhoneOutlined style={{ marginInlineEnd: token.marginXXS }} />
                  {record.anonymousPhone}
                </Text>
              )}
            </div>
          </Flex>
        );
      }
    },
    {
      title: t('videoMeetings.table.meetingDetails'),
      key: 'details',
      render: (_: unknown, record: Appointment) => {
        const title = getMeetingTitle(record, t);
        const description = getMeetingDescription(record, t);
        const duration = getAppointmentDurationMinutes(record);
        return (
          <Flex vertical gap={token.marginXXS} style={{ maxWidth: 280 }}>
            <Text strong ellipsis={{ tooltip: title }}>
              {title}
            </Text>
            {description && (
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }} ellipsis={{ tooltip: description }}>
                {description}
              </Text>
            )}
            <Text type="secondary" style={{ fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}>
              <ClockCircleOutlined style={{ marginInlineEnd: token.marginXXS }} />
              {t('videoMeetings.table.minutes', { count: duration })}
            </Text>
          </Flex>
        );
      }
    },
    {
      title: t('videoMeetings.table.dateTime'),
      key: 'datetime',
      render: (_: unknown, record: Appointment) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <div style={{ fontWeight: 500 }}>{dayjs(record.startTime).format('MMM DD, YYYY')}</div>
          <Text type="secondary" style={{ display: 'block', fontSize: token.fontSizeSM }}>
            {dayjs(record.startTime).format('HH:mm')} – {dayjs(record.endTime).format('HH:mm')}
            <Text type="secondary" style={{ fontSize: token.fontSizeSM, color: token.colorTextTertiary }}>
              {' · '}
              {dayjs(record.startTime).fromNow()}
            </Text>
          </Text>
        </div>
      )
    },
    {
      title: t('videoMeetings.table.status'),
      key: 'status',
      render: (_: unknown, record: Appointment) => (
        <Tag color={getStatusTagColor(record.status)}>{getAppointmentStatusLabel(record.status, t)}</Tag>
      )
    },
    {
      title: t('videoMeetings.table.actions'),
      key: 'actions',
      fixed: pinColumn('right'),
      render: (_: unknown, record: Appointment) => {
        const meeting = getMeetingInfo(asAppointmentWithContext(record));
        const hasJoinUrl = Boolean(meeting.meetingModeratorUrl || meeting.meetingUrl);
        const hasCopyUrl = Boolean(meeting.meetingUrl || meeting.meetingModeratorUrl);
        return (
          <Flex gap={token.marginXS} align="center">
            {hasJoinUrl && (
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleJoinMeeting(record)}
              >
                {t('videoMeetings.actions.joinMeeting')}
              </Button>
            )}
            <Tooltip title={t('videoMeetings.actions.viewDetails')}>
              <Button
                size="small"
                icon={<EyeOutlined />}
                aria-label={t('videoMeetings.actions.viewDetails')}
                onClick={(event) => handleViewDetails(record, event)}
              />
            </Tooltip>
            {hasCopyUrl && (
              <Tooltip title={t('videoMeetings.actions.copyMeetingUrl')}>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  aria-label={t('videoMeetings.actions.copyMeetingUrl')}
                  onClick={() => handleCopyUrl(record)}
                />
              </Tooltip>
            )}
          </Flex>
        );
      }
    }
  ];

  const stats = {
    total: appointments.length,
    scheduled: appointments.filter((a) => a.status === 'SCHEDULED').length,
    confirmed: appointments.filter((a) => a.status === 'CONFIRMED').length,
    today: appointments.filter((a) => dayjs(a.startTime).isSame(dayjs(), 'day')).length
  };

  const hasActiveFilters = !!filters.search || !!filters.status || !!filters.dateRange;
  const clearFilters = () => setFilters({ status: undefined, search: '', dateRange: undefined });

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<VideoCameraOutlined />}
        title={t('videoMeetings.title')}
        description={t('videoMeetings.description')}
        actions={
          <AutoRefreshToggle
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            isFetching={isFetching}
            lastUpdated={lastUpdatedText}
          />
        }
      />

      <StatCardGrid>
        <StatCard
          label={t('videoMeetings.stats.totalMeetings')}
          value={stats.total}
          icon={<VideoCameraOutlined />}
          color={token.colorPrimary}
          loading={isLoading}
        />
        <StatCard
          label={t('videoMeetings.stats.scheduled')}
          value={stats.scheduled}
          icon={<CalendarOutlined />}
          color={token.colorInfo}
          loading={isLoading}
        />
        <StatCard
          label={t('videoMeetings.stats.confirmed')}
          value={stats.confirmed}
          icon={<CheckCircleOutlined />}
          color={token.colorSuccess}
          loading={isLoading}
        />
        <StatCard
          label={t('videoMeetings.stats.today')}
          value={stats.today}
          icon={<ClockCircleOutlined />}
          color={token.colorWarning}
          loading={isLoading}
        />
      </StatCardGrid>

      <FilterBar
        actions={
          hasActiveFilters ? (
            <Button icon={<ClearOutlined />} onClick={clearFilters}>
              {t('videoMeetings.filters.clear')}
            </Button>
          ) : undefined
        }
      >
        <Input
          placeholder={t('videoMeetings.filters.searchPlaceholder')}
          prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          allowClear
          style={{ flex: '1 1 220px', minWidth: 160, maxWidth: 400 }}
        />
        <Select
          placeholder={t('videoMeetings.filters.statusPlaceholder')}
          value={filters.status}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
          allowClear
          style={{ flex: '1 1 140px', minWidth: 130, maxWidth: 220 }}
          options={[
            { value: 'SCHEDULED', label: t('videoMeetings.filters.scheduled') },
            { value: 'CONFIRMED', label: t('videoMeetings.filters.confirmed') },
            { value: 'COMPLETED', label: t('videoMeetings.filters.completed') },
            { value: 'CANCELLED', label: t('videoMeetings.filters.cancelled') },
            { value: 'NO_SHOW', label: t('videoMeetings.filters.noShow') }
          ]}
        />
        <RangePicker
          value={filters.dateRange}
          onChange={(dates) =>
            setFilters((prev) => ({
              ...prev,
              dateRange: dates as [dayjs.Dayjs, dayjs.Dayjs] | undefined
            }))
          }
          style={{ flex: '1 1 240px', minWidth: 0, maxWidth: 320 }}
          placeholder={[t('videoMeetings.filters.startDate'), t('videoMeetings.filters.endDate')]}
        />
      </FilterBar>

      <Card
        title={
          <Flex align="center" gap={token.marginXS}>
            <span>{t('videoMeetings.table.title')}</span>
            {!isLoading && (
              <Tag color={hasActiveFilters ? 'blue' : 'default'} style={{ margin: 0 }}>
                {appointments.length}
              </Tag>
            )}
          </Flex>
        }
        styles={{ body: { padding: 0 } }}
      >
        <Table
          columns={columns}
          dataSource={appointments}
          rowKey="id"
          loading={isLoading}
          pagination={{
            defaultPageSize: 10,
            showQuickJumper: true,
            responsive: true,
            style: { paddingInline: token.paddingMD },
            showTotal: (total, range) => (
              <span style={{ color: token.colorTextSecondary }}>
                {t('videoMeetings.pagination', { start: range[0], end: range[1], total })}
              </span>
            )
          }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: isLoading ? (
              <div style={{ minHeight: 160 }} />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={hasActiveFilters ? t('videoMeetings.table.noMatchFilters') : t('videoMeetings.table.empty')}
              >
                {hasActiveFilters && (
                  <Button icon={<ClearOutlined />} onClick={clearFilters}>
                    {t('videoMeetings.filters.clear')}
                  </Button>
                )}
              </Empty>
            )
          }}
        />
      </Card>
    </PageTransition>
  );
}
